import type { MarginMarketInfo } from "@yutai/shared"

// 優待クロス（現物買い＋売建）の費用/損益の概算。費用は売建側の貸株料等と、
// 現物買いを証券担保ローンで賄う想定の借入金利（手数料・配当調整金は含めない）。
//
// 楽天証券の貸株料（年率）: 一般信用「短期（14日）」3.90% / 一般信用「無期限」1.10%。
// 一般信用に逆日歩は発生しない。特別空売り料は 1株あたり1日の金額
// （CSVの値は日々変わるので、アップロード時点の参考値）。

export type CrossKind = "general14d" | "generalUnlimited"

export const CROSS_KIND_LABELS: Record<CrossKind, string> = {
  general14d: "一般 14日",
  generalUnlimited: "一般 無期限",
}

export const LENDING_FEE_RATE: Record<CrossKind, number> = {
  general14d: 0.039,
  generalUnlimited: 0.011,
}

/** 証券担保ローンの金利（年率）。借入額に応じて変わる。現物買い金額（= 売建金額）を借りる想定 */
export const LOAN_RATE_TIERS = [
  { rate: 0.02375, label: "1,000万円超" },
  { rate: 0.03375, label: "100万円超〜1,000万円" },
  { rate: 0.04375, label: "100万円以下" },
] as const

export const DEFAULT_LOAN_RATE = 0.03375

export interface CrossCostInput {
  kind: CrossKind
  /** 優待に必要な最低株数 */
  shares: number
  market: Pick<MarginMarketInfo, "price" | "specialFee">
  /** 売建の保有日数（暦日） */
  days: number
  /** 優待の価値[円]。不明なら null */
  benefitValue: number | null
  /** 証券担保ローンの年率。省略時は DEFAULT_LOAN_RATE */
  loanRate?: number
}

export interface CrossCost {
  /** 売建金額 = 現在値 × 株数 */
  sellAmount: number
  /** 貸株料（保有日数分） */
  lendingFee: number
  /** 特別空売り料（保有日数分） */
  extraFee: number
  /** 証券担保ローンの利息（現物買い金額 × 年率 × 保有日数/365） */
  loanFee: number
  cost: number
  /** 優待価値 − 費用。優待価値が不明なら null */
  profit: number | null
  /** 優待価値と費用が釣り合う保有日数。1日あたり費用が0なら null（何日でも赤字にならない） */
  breakEvenDays: number | null
}

export function calcCrossCost({
  kind,
  shares,
  market,
  days,
  benefitValue,
  loanRate = DEFAULT_LOAN_RATE,
}: CrossCostInput): CrossCost {
  const sellAmount = market.price * shares
  const lendingPerDay = (sellAmount * LENDING_FEE_RATE[kind]) / 365
  const extraPerDay = market.specialFee * shares
  const loanPerDay = (sellAmount * loanRate) / 365
  const lendingFee = lendingPerDay * days
  const extraFee = extraPerDay * days
  const loanFee = loanPerDay * days
  const cost = lendingFee + extraFee + loanFee
  const perDay = lendingPerDay + extraPerDay + loanPerDay
  return {
    sellAmount,
    lendingFee,
    extraFee,
    loanFee,
    cost,
    profit: benefitValue === null ? null : benefitValue - cost,
    breakEvenDays: benefitValue === null || perDay <= 0 ? null : benefitValue / perDay,
  }
}

function utcDate(y: number, m: number, d: number): Date {
  return new Date(Date.UTC(y, m - 1, d))
}

/**
 * CSVの優待権利付き最終日 "MM/DD" を、基準日（YYYY-MM-DD）以降で最初に来る日付にする。
 * CSVは年を持たないため。例: 基準日 2026-10-05 の "03/29" → 2027-03-29
 */
export function resolveRightsDate(rightsDate: string, baseDate: string): string | null {
  const m = rightsDate.match(/^(\d{2})\/(\d{2})$/)
  const b = baseDate.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m || !b) return null
  const month = Number(m[1])
  const day = Number(m[2])
  let year = Number(b[1])
  const base = utcDate(year, Number(b[2]), Number(b[3]))
  let d = utcDate(year, month, day)
  if (d < base) {
    year += 1
    d = utcDate(year, month, day)
  }
  if (d.getUTCMonth() + 1 !== month || d.getUTCDate() !== day) return null // 02/30 など
  return d.toISOString().slice(0, 10)
}

const DAY_MS = 24 * 60 * 60 * 1000

function parseIso(iso: string): number {
  return Date.parse(`${iso}T00:00:00Z`)
}

export function addDays(iso: string, n: number): string {
  return new Date(parseIso(iso) + n * DAY_MS).toISOString().slice(0, 10)
}

export function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((parseIso(toIso) - parseIso(fromIso)) / DAY_MS)
}

/** 翌営業日（土日だけ飛ばす。祝日は考慮しない） */
export function nextBusinessDay(iso: string): string {
  let d = addDays(iso, 1)
  while ([0, 6].includes(new Date(`${d}T00:00:00Z`).getUTCDay())) d = addDays(d, 1)
  return d
}

/** 権利付き最終日に建てて翌営業日（権利落ち日）に返済する場合の保有日数。金曜なら3日、他は1日 */
export function defaultHoldingDays(rightsDateIso: string): number {
  return daysBetween(rightsDateIso, nextBusinessDay(rightsDateIso))
}

/** 一般信用14日の返済期限（暦日）。これを超えて持つなら無期限を使う */
export const GENERAL_14D_MAX_DAYS = 14

/** 区分ごとの最長保有日数（超えると建てられない）。一般14日のみ上限を持つ */
export function maxHoldingDays(kind: CrossKind): number {
  return kind === "general14d" ? GENERAL_14D_MAX_DAYS : Number.POSITIVE_INFINITY
}

/**
 * 権利付き最終日の daysBefore 日前に建てたとして、黒字のまま建てられる最も早い「何日前」。
 * 当日建てでも赤字なら null。14日の期限も考慮する。
 */
export function earliestProfitableDaysBefore(
  kind: CrossKind,
  breakEvenDays: number | null,
  baseDays: number
): number | null {
  const byProfit = breakEvenDays === null ? Number.POSITIVE_INFINITY : Math.floor(breakEvenDays)
  const limit = Math.min(byProfit, maxHoldingDays(kind)) - baseDays
  return limit >= 0 ? limit : null
}
